/**
 * Tenant Library — System tab (PR-J).
 *
 * Read-only view of platform-published system Library content. Every
 * row is badged "System" with attribution "Platform Admin"; the
 * tenant cannot edit, delete, or manage these rows. Mutate buttons
 * are hidden — sysadmin owns the full lifecycle.
 *
 * Sub-views:
 *   - Articles: published system wiki articles + folders
 *   - Documents: published system uploaded documents
 *   - Sources: published system connectors (informational — tenants
 *     don't sync from these directly; sysadmin owns that loop)
 *
 * I-M6.7 redaction is handled server-side in /library/system/* — this
 * UI just renders what the API returns. publishedByLabel is always
 * "Platform Admin"; audit metadata is never returned.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  BookOpen, FileText, Cloud, Globe, Search, Loader2, FolderOpen,
  Lock,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { sanitizeHtml } from '@/lib/sanitize';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  useTenantSystemArticles,
  useTenantSystemArticle,
  useTenantSystemFolders,
  useTenantSystemDocuments,
  useTenantSystemSources,
  type SystemLibArticleListItem,
} from '@/lib/use-tenant-system-library';
import { documentTypeLabel, useDocumentTypes } from '@/lib/use-document-types';

export const Route = createFileRoute('/library/system')({
  component: TenantSystemLibraryPage,
});

function TenantSystemLibraryPage() {
  const [tab, setTab] = useState<'articles' | 'documents' | 'sources'>('articles');
  const [search, setSearch] = useState('');
  const [openArticleId, setOpenArticleId] = useState<string | null>(null);

  return (
    <div className="p-6 space-y-4">
      {/* Header strip — explains the System Library to tenants */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 p-3 flex items-start gap-3">
        <Globe className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
        <div className="text-xs text-muted-foreground">
          <p className="font-medium text-foreground mb-0.5">System Library</p>
          <p>
            Content published by the platform — regulations, SOPs, reference docs, ANSI standards.
            Read-only. Edits and removals are managed by Platform Admin.
          </p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search system content..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9"
          aria-label="Search system Library"
        />
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="articles" className="gap-1.5"><BookOpen className="h-3.5 w-3.5" />Articles</TabsTrigger>
          <TabsTrigger value="documents" className="gap-1.5"><FileText className="h-3.5 w-3.5" />Documents</TabsTrigger>
          <TabsTrigger value="sources" className="gap-1.5"><Cloud className="h-3.5 w-3.5" />Sources</TabsTrigger>
        </TabsList>
        <TabsContent value="articles" className="mt-4">
          <ArticlesTab search={search} onOpenArticle={setOpenArticleId} />
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <DocumentsTab search={search} />
        </TabsContent>
        <TabsContent value="sources" className="mt-4">
          <SourcesTab search={search} />
        </TabsContent>
      </Tabs>

      {openArticleId && (
        <ArticleViewerDialog id={openArticleId} onClose={() => setOpenArticleId(null)} />
      )}
    </div>
  );
}

// ─── Articles ──────────────────────────────────────────────────────────────

function ArticlesTab({ search, onOpenArticle }: { search: string; onOpenArticle: (id: string) => void }) {
  const articlesQ = useTenantSystemArticles();
  const foldersQ = useTenantSystemFolders();

  const folderMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of foldersQ.data?.data ?? []) m.set(f.id, f.name);
    return m;
  }, [foldersQ.data]);

  const filtered = useMemo(() => {
    const items = articlesQ.data?.data ?? [];
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((a) => a.title.toLowerCase().includes(s));
  }, [articlesQ.data, search]);

  if (articlesQ.isLoading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Title</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Folder</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Source</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Published</th>
            <th className="w-10 px-3 py-2.5" aria-label="Row actions"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={5} className="p-12 text-center text-muted-foreground">
                {(articlesQ.data?.data ?? []).length === 0
                  ? 'No system articles published yet.'
                  : 'No system articles match your search.'}
              </td>
            </tr>
          ) : filtered.map((a: SystemLibArticleListItem) => (
            <tr key={a.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => onOpenArticle(a.id)}>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-3.5 w-3.5 text-purple-500" />
                  <span className="font-medium hover:underline">{a.title}</span>
                </div>
                {a.excerpt && <p className="text-[10px] text-muted-foreground line-clamp-1 ml-5">{a.excerpt}</p>}
              </td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">
                {a.folderId ? folderMap.get(a.folderId) ?? '—' : <span className="italic">root</span>}
              </td>
              <td className="px-3 py-2.5"><SystemBadge /></td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">
                {a.publishedAt ? new Date(a.publishedAt).toLocaleDateString() : '—'}
                <p className="text-[10px]">by {a.publishedByLabel}</p>
              </td>
              <td className="px-3 py-2.5">
                <span title="Read-only — published by Platform Admin"><Lock className="h-3.5 w-3.5 text-muted-foreground" /></span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArticleViewerDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useTenantSystemArticle(id);
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {q.data?.title ?? 'System Article'}
            <SystemBadge />
          </DialogTitle>
          <DialogDescription>
            {q.data ? <>Published by {q.data.publishedByLabel} · v{q.data.version}</> : 'Loading…'}
          </DialogDescription>
        </DialogHeader>
        {q.isLoading ? (
          <div className="flex items-center justify-center h-32"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : q.error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Failed to load: {q.error.message}
          </div>
        ) : q.data ? (
          <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(q.data.content) }} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ─── Documents ─────────────────────────────────────────────────────────────

export function DocumentsTab({ search }: { search: string }) {
  const q = useTenantSystemDocuments();
  // A document's type reads as its catalogue row's label, as on the detail
  // page's badge (L6-13; reviews LB6T10-R3 / LB6-T10-BF-04). The tenant's
  // catalogue holds every built-in, so it names a System Library type too.
  const types = useDocumentTypes();
  const filtered = useMemo(() => {
    const items = q.data?.data ?? [];
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((d) => d.title.toLowerCase().includes(s) || d.fileName.toLowerCase().includes(s));
  }, [q.data, search]);

  if (q.isLoading) return <div className="flex items-center justify-center h-32"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Document</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Type</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Source</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Published</th>
            <th className="w-10 px-3 py-2.5" aria-label="Row actions"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={5} className="p-12 text-center text-muted-foreground">
                {(q.data?.data ?? []).length === 0
                  ? 'No system documents published yet.'
                  : 'No system documents match your search.'}
              </td>
            </tr>
          ) : filtered.map((d) => (
            <tr key={d.id} className="hover:bg-muted/30">
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-blue-500" /><span className="font-medium">{d.title}</span></div>
                <p className="text-[10px] text-muted-foreground ml-5">{d.fileName}</p>
              </td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">{d.documentType ? documentTypeLabel(types.data, d.documentType) : d.mimeType}</td>
              <td className="px-3 py-2.5"><SystemBadge /></td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">
                {d.publishedAt ? new Date(d.publishedAt).toLocaleDateString() : '—'}
                <p className="text-[10px]">by {d.publishedByLabel}</p>
              </td>
              <td className="px-3 py-2.5">
                <span title="Read-only — published by Platform Admin"><Lock className="h-3.5 w-3.5 text-muted-foreground" /></span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Sources ───────────────────────────────────────────────────────────────

function SourcesTab({ search }: { search: string }) {
  const q = useTenantSystemSources();
  const filtered = useMemo(() => {
    const items = q.data?.data ?? [];
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((src) => src.displayName.toLowerCase().includes(s));
  }, [q.data, search]);

  if (q.isLoading) return <div className="flex items-center justify-center h-32"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Source</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Kind</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Schedule</th>
            <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Source</th>
            <th className="w-10 px-3 py-2.5" aria-label="Row actions"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={5} className="p-12 text-center text-muted-foreground">
                {(q.data?.data ?? []).length === 0
                  ? 'No system sources published yet.'
                  : 'No system sources match your search.'}
              </td>
            </tr>
          ) : filtered.map((s) => (
            <tr key={s.id} className="hover:bg-muted/30">
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2"><Cloud className="h-3.5 w-3.5 text-sky-500" /><span className="font-medium">{s.displayName}</span></div>
              </td>
              <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px]">{s.connectorKind}</Badge></td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">{s.syncSchedule}</td>
              <td className="px-3 py-2.5"><SystemBadge /></td>
              <td className="px-3 py-2.5">
                <span title="Read-only — published by Platform Admin"><Lock className="h-3.5 w-3.5 text-muted-foreground" /></span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Shared "System" badge ─────────────────────────────────────────────────

function SystemBadge() {
  return (
    <Badge variant="outline" className="gap-1 text-[10px] bg-primary/10 text-primary border-primary/30">
      <Globe className="h-3 w-3" />
      System
    </Badge>
  );
}
